import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '../../ui/ModalProject/Modal';
import { Button } from '../../ui/Button/Button';
import { IconClose, IconPlus } from '../../../assets/icons/icons';
import { useToast } from '../../ui/Toast/ToastProvider';
import { useAppSelector } from '../../../app/hooks';
import { selectOrganizationId } from '../../../entities/organization/model/slice';
import {
    useCreateAnalogDimensionsMutation,
    type AnalogDimensionsBody,
    type AnalogDimensionsFieldErrors,
} from '../../../entities/project/api/analogDimensionsApi';
import type { ProductAnalog } from '../../../types/results';
import { formatNumber } from '../../../utils/format';
import shell from '../ManualPositionsModal/ManualPositionsModal.module.scss';
import styles from './AnalogDimensionsModal.module.scss';

interface AnalogDimensionsModalProps {
    analog: { item: ProductAnalog; index: number } | null;
    projectId: string;
    productId: string;
    onClose: () => void;
    onSaved?: () => void;
}

type SizeKey = 'length' | 'width' | 'height' | 'weight';
type Group = 'correct' | 'transport';
type FieldKey = `${Group}_${SizeKey}`;

type FormState = Record<FieldKey, string>;
type FormErrors = Partial<Record<FieldKey, string>>;

const MAX_MM = 10_000;
const MAX_G = 5_000_000;

const SIZE_LABEL: Record<SizeKey, string> = {
    length: 'Длина, мм',
    width: 'Ширина, мм',
    height: 'Высота, мм',
    weight: 'Вес, г',
};

const SIZE_KEYS: SizeKey[] = ['length', 'width', 'height', 'weight'];

const EMPTY: FormState = {
    correct_length: '',
    correct_width: '',
    correct_height: '',
    correct_weight: '',
    transport_length: '',
    transport_width: '',
    transport_height: '',
    transport_weight: '',
};

const asInput = (value: number | null | undefined): string =>
    typeof value === 'number' && value > 0 ? String(value) : '';

const analogLabel = (item: ProductAnalog) =>
    [item.brand, item.model].filter(Boolean).join(' ') || item.host || item.title;

const validateField = (raw: string, key: SizeKey, required: boolean): string | null => {
    const value = raw.trim();

    if (!value) return required ? 'Укажите вес' : null;
    if (!/^\d+$/.test(value)) return 'Только целое число';

    const number = Number(value);
    if (number <= 0) return 'Не может быть равно 0';

    const max = key === 'weight' ? MAX_G : MAX_MM;
    if (number > max) return `Не больше ${formatNumber(max)}`;

    return null;
};

const isFieldErrors = (data: unknown): data is AnalogDimensionsFieldErrors =>
    typeof data === 'object' && data !== null && Object.values(data).every(Array.isArray);

export function AnalogDimensionsModal({ analog, projectId, productId, onClose, onSaved }: AnalogDimensionsModalProps) {
    const notify = useToast();
    const organizationId = useAppSelector(selectOrganizationId);
    const [create, { isLoading }] = useCreateAnalogDimensionsMutation();

    const [form, setForm] = useState<FormState>(EMPTY);
    const [errors, setErrors] = useState<FormErrors>({});

    const item = analog?.item ?? null;

    useEffect(() => {
        if (!item) return;

        setForm({
            correct_length: asInput(item.dimensions?.length),
            correct_width: asInput(item.dimensions?.width),
            correct_height: asInput(item.dimensions?.height),
            correct_weight: asInput(item.dimensions?.weight),
            transport_length: asInput(item.transport?.length),
            transport_width: asInput(item.transport?.width),
            transport_height: asInput(item.transport?.height),
            transport_weight: asInput(item.transport?.weight),
        });
        setErrors({});
    }, [item]);

    const patch = (key: FieldKey, value: string) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
    };

    const validate = (): FormErrors => {
        const next: FormErrors = {};

        SIZE_KEYS.forEach((key) => {
            const correct = validateField(form[`correct_${key}`], key, key === 'weight');
            if (correct) next[`correct_${key}`] = correct;

            const transport = validateField(form[`transport_${key}`], key, false);
            if (transport) next[`transport_${key}`] = transport;
        });

        return next;
    };

    const canSubmit = useMemo(
        () => Boolean(item && organizationId && form.correct_weight.trim()) && !isLoading,
        [item, organizationId, form.correct_weight, isLoading],
    );

    const handleSubmit = async () => {
        if (!item || !organizationId) return;

        const nextErrors = validate();
        if (Object.keys(nextErrors).length) {
            setErrors(nextErrors);
            return;
        }

        const body: AnalogDimensionsBody = {
            project_id: projectId,
            organization_id: organizationId,
            product_id: productId,
            analog_url: item.url,
            correct_weight: Number(form.correct_weight),
        };

        (['length', 'width', 'height'] as const).forEach((key) => {
            const value = form[`correct_${key}`].trim();
            if (value) body[`correct_${key}`] = Number(value);
        });

        SIZE_KEYS.forEach((key) => {
            const value = form[`transport_${key}`].trim();
            if (value) body[`transport_${key}`] = Number(value);
        });

        try {
            await create(body).unwrap();
            notify('Габариты аналога отправлены, доставка пересчитывается');
            onSaved?.();
            onClose();
        } catch (error) {
            const status = (error as { status?: number }).status;
            const data = (error as { data?: unknown }).data;

            if (status === 400 && isFieldErrors(data)) {
                const fromServer: FormErrors = {};
                (Object.keys(data) as (keyof AnalogDimensionsFieldErrors)[]).forEach((key) => {
                    const message = data[key]?.[0];
                    if (message && key in EMPTY) fromServer[key as FieldKey] = message;
                });
                setErrors(fromServer);
                if (!Object.keys(fromServer).length) notify('Проверьте введённые значения');
                return;
            }

            const message =
                typeof data === 'string'
                    ? data
                    : (data as { message?: string; error?: string } | undefined)?.message ??
                      (data as { message?: string; error?: string } | undefined)?.error;

            notify(
                status === 403
                    ? 'Нет прав на изменение габаритов в этой организации'
                    : message || 'Не удалось сохранить габариты аналога',
            );
        }
    };

    const renderField = (group: Group, key: SizeKey) => {
        const fieldKey: FieldKey = `${group}_${key}`;
        const error = errors[fieldKey];
        const required = group === 'correct' && key === 'weight';

        return (
            <label key={fieldKey} className={styles.field}>
                <span>
                    {SIZE_LABEL[key]}
                    {required && <i aria-hidden="true">*</i>}
                </span>
                <input
                    type="text"
                    inputMode="numeric"
                    value={form[fieldKey]}
                    disabled={isLoading}
                    aria-invalid={Boolean(error)}
                    onChange={(event) => patch(fieldKey, event.target.value)}
                />
                {error && <em className={styles.error}>{error}</em>}
            </label>
        );
    };

    return (
        <Modal open={Boolean(analog)} onClose={onClose} overlayClassName={shell.overlay} bare>
            <div className={`${shell.manual} ${styles.window}`} role="dialog" aria-modal="true" aria-label="Габариты аналога">
                <div className={shell.head}>
                    <div className={shell.icon}>
                        <IconPlus />
                    </div>
                    <div>
                        <h3>{analog ? `Габариты аналога №${analog.index + 1}` : 'Габариты аналога'}</h3>
                        {item && <p>{analogLabel(item)}</p>}
                    </div>
                    <div className={shell.spacer} />
                    <button type="button" className={shell.close} aria-label="Закрыть окно" onClick={onClose}>
                        <IconClose />
                    </button>
                </div>

                <div className={shell.body}>
                    <div className={styles.card}>
                        <section className={styles.group}>
                            <h4 className={styles.groupTitle}>Габариты товара</h4>
                            <div className={styles.grid}>{SIZE_KEYS.map((key) => renderField('correct', key))}</div>
                            <p className={styles.hint}>Пустое поле оставит прежнее значение. Вес обязателен.</p>
                        </section>

                        <section className={styles.group}>
                            <h4 className={styles.groupTitle}>Транспортные габариты</h4>
                            <div className={styles.grid}>{SIZE_KEYS.map((key) => renderField('transport', key))}</div>
                            <p className={styles.hint}>
                                Необязательно. Если заполнить — применятся сразу, без проверки, и аналог перестанет
                                пересчитываться по правилам упаковки.
                            </p>
                        </section>
                    </div>
                </div>

                <div className={shell.foot}>
                    <span className={shell.note}>Значения уйдут на проверку, доставка по аналогу пересчитается сразу.</span>
                    <div className={shell.footRight}>
                        <Button onClick={onClose} disabled={isLoading}>
                            Отмена
                        </Button>
                        <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
                            {isLoading && <span className={shell.spinner} aria-hidden="true" />}
                            {isLoading ? 'Сохраняем…' : 'Сохранить'}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}